import { useQuery } from '@tanstack/react-query';
import { Button, Card, Skeleton } from '@recipes/ui';
import { format } from 'date-fns';
import {
  CalendarDays,
  ChefHat,
  Link2,
  MapPin,
  Plus,
  RefreshCw,
  ScanBarcode,
  Sparkles,
  WifiOff,
} from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { Illustration } from '@/features/appearance/illustration';
import { LiveRegion } from '@/components/feedback/live-region';
import { HUE_SURFACE } from '@/features/appearance/hue-classes';
import { ThemePicture } from '@/features/appearance/theme-picture';
import { useAppearance } from '@/features/appearance/use-appearance';
import { InstallPrompt } from '@/components/pwa/install-prompt';
import { RecipeRow } from '@/features/recipes/recipe-row';
import { useFeature } from '@/features/shared/hooks';
import { startTastingSync } from '@/features/tasting/capture-queue';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth/auth-provider';
import { useOnlineStatus } from '@/lib/hooks/use-online-status';
import { usePendingActionCount } from '@/lib/offline/hooks';
import { replayPendingActions, startSyncManager } from '@/lib/offline/queue';
import { queryKeys } from '@/lib/query-keys';

function TodayPlan() {
  const { t } = useTranslation();
  const today = format(new Date(), 'yyyy-MM-dd');
  const plan = useQuery({
    queryKey: queryKeys.mealPlan(today, today),
    queryFn: () => api.mealPlan.week({ from: today, to: today }),
    retry: false,
  });
  const entries = plan.data?.entries ?? [];
  return (
    <section aria-labelledby="today-heading" data-testid="today-plan">
      <div className="mb-2 flex items-center justify-between">
        <h2 id="today-heading" className="text-lg font-semibold">
          {t('dashboard.today')}
        </h2>
        <Button asChild variant="ghost" size="sm">
          <Link to="/meal-plan">{t('nav.mealPlan')}</Link>
        </Button>
      </div>
      {plan.isPending ? (
        <Skeleton className="h-16" />
      ) : plan.isError ? (
        <p className="text-sm text-muted-foreground">{t('dashboard.planUnavailable')}</p>
      ) : entries.length === 0 ? (
        <div className="flex items-center gap-4 rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
          <Illustration name="mealPlan" className="h-16" />
          <p>{t('dashboard.nothingPlanned')}</p>
        </div>
      ) : (
        <ul className="divide-y rounded-lg border">
          {entries.map((entry) => (
            <li key={entry.id} className="flex items-center gap-3 p-3">
              <span className="w-24 shrink-0 text-xs font-medium uppercase text-muted-foreground">
                {entry.slot === 'CUSTOM' ? entry.customSlotName : t(`dashboard.slot.${entry.slot}`)}
              </span>
              {entry.recipeThumbnailUrl && (
                <img
                  src={entry.recipeThumbnailUrl}
                  alt=""
                  className="size-10 rounded object-cover"
                />
              )}
              {entry.recipeId ? (
                <Link
                  to={`/recipes/${entry.recipeId}`}
                  className="font-medium underline-offset-4 hover:underline"
                >
                  {entry.recipeTitle ?? entry.title}
                </Link>
              ) : (
                <span className="font-medium">{entry.title}</span>
              )}
              {entry.recipeId && (
                <Button asChild size="sm" variant="outline" className="ml-auto">
                  <Link to={`/cook/${entry.recipeId}`}>
                    <ChefHat aria-hidden /> {t('dashboard.cook')}
                  </Link>
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Morning until 11, evening from 18 – by the device's clock. */
function daytime(now = new Date()): 'morning' | 'day' | 'evening' {
  const hour = now.getHours();
  return hour < 11 ? 'morning' : hour < 18 ? 'day' : 'evening';
}

export function DashboardPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const online = useOnlineStatus();
  const pending = usePendingActionCount();

  useEffect(() => startSyncManager(), []);
  // Tasting notes captured offline are synchronized from here as well.
  useEffect(() => startTastingSync(), []);
  const tastingEnabled = useFeature('tasting_notes');
  const catalogEnabled = useFeature('ingredient_catalog');
  const { theme } = useAppearance();

  return (
    <div className="mx-auto max-w-5xl space-y-8 p-4 md:p-6" data-testid="dashboard-page">
      <LiveRegion />
      {/* The greeting band: the words on a surface of their own, the theme's picture beside them. */}
      <header
        className="flex overflow-hidden rounded-xl border bg-card"
        data-testid="dashboard-greeting"
      >
        <div className="min-w-0 flex-1 p-5 md:p-6">
          <p className="text-sm font-medium text-primary">{t(`dashboard.daytime.${daytime()}`)}</p>
          <h1 className="text-2xl font-bold leading-tight">
            {t('dashboard.greeting', { name: user?.displayName ?? '' })}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('dashboard.subtitle')}</p>
        </div>
        <ThemePicture theme={theme} className="hidden h-auto w-2/5 shrink-0 sm:block" />
      </header>

      <InstallPrompt />

      {(!online || pending > 0) && (
        <div
          role="status"
          className="flex flex-wrap items-center gap-3 rounded-lg border bg-muted/50 p-3 text-sm"
        >
          <WifiOff className="size-4" aria-hidden />
          <span className="flex-1">
            {!online ? t('offline.dashboardHint') : t('offline.pendingCount', { count: pending })}
          </span>
          {online && pending > 0 && (
            <Button size="sm" variant="outline" onClick={() => void replayPendingActions()}>
              <RefreshCw aria-hidden /> {t('offline.syncNow')}
            </Button>
          )}
          <Button asChild size="sm" variant="ghost">
            <Link to="/offline">{t('offline.title')}</Link>
          </Button>
        </div>
      )}

      <section aria-labelledby="quick-actions-heading">
        <h2 id="quick-actions-heading" className="sr-only">
          {t('dashboard.quickActions')}
        </h2>
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
          <Card className="p-0">
            <Link
              to="/recipes/new"
              className="flex min-h-[88px] flex-col items-center justify-center gap-1 p-3 text-center text-sm font-medium hover:bg-accent"
              data-testid="quick-add"
            >
              <span
                className={`flex size-11 items-center justify-center rounded-full ${HUE_SURFACE[7]}`}
              >
                <Plus className="size-6" aria-hidden />
              </span>
              {t('dashboard.actions.add')}
            </Link>
          </Card>
          <Card className="p-0">
            <Link
              to="/add/import"
              className="flex min-h-[88px] flex-col items-center justify-center gap-1 p-3 text-center text-sm font-medium hover:bg-accent"
            >
              <span
                className={`flex size-11 items-center justify-center rounded-full ${HUE_SURFACE[3]}`}
              >
                <Link2 className="size-6" aria-hidden />
              </span>
              {t('dashboard.actions.import')}
            </Link>
          </Card>
          <Card className="p-0">
            <Link
              to="/add/ask"
              className="flex min-h-[88px] flex-col items-center justify-center gap-1 p-3 text-center text-sm font-medium hover:bg-accent"
            >
              <span
                className={`flex size-11 items-center justify-center rounded-full ${HUE_SURFACE[8]}`}
              >
                <Sparkles className="size-6" aria-hidden />
              </span>
              {t('dashboard.actions.ai')}
            </Link>
          </Card>
          <Card className="p-0">
            <Link
              to="/cook"
              className="flex min-h-[88px] flex-col items-center justify-center gap-1 p-3 text-center text-sm font-medium hover:bg-accent"
              data-testid="quick-cook"
            >
              <span
                className={`flex size-11 items-center justify-center rounded-full ${HUE_SURFACE[1]}`}
              >
                <ChefHat className="size-6" aria-hidden />
              </span>
              {t('dashboard.actions.cook')}
            </Link>
          </Card>
        </div>
      </section>

      <TodayPlan />

      <RecipeRow
        title={t('dashboard.recent')}
        params={{ sort: 'recentlyUpdated' }}
        seeAllTo="/recipes?sort=recentlyUpdated"
        emptyText={t('dashboard.emptyRecent')}
        emptyAction={
          <Button asChild size="sm">
            <Link to="/recipes/new">{t('dashboard.actions.add')}</Link>
          </Button>
        }
        testId="recent-recipes"
      />
      <RecipeRow
        title={t('dashboard.favorites')}
        params={{ favorite: true, sort: 'recentlyUpdated' }}
        seeAllTo="/recipes?favorite=1"
        emptyText={t('dashboard.emptyFavorites')}
        testId="favorite-recipes"
      />
      <RecipeRow
        title={t('dashboard.lastCooked')}
        params={{ sort: 'lastCooked', order: 'desc' }}
        seeAllTo="/recipes?sort=lastCooked"
        emptyText={t('dashboard.emptyCooked')}
        testId="last-cooked-recipes"
      />
      <section className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground">
        <Link
          to="/cooking-log"
          className="inline-flex items-center gap-1 underline-offset-4 hover:underline"
        >
          <CalendarDays className="size-4" aria-hidden /> {t('dashboard.cookingLog')}
        </Link>
        {catalogEnabled && (
          <Link
            to="/catalog/ingredients"
            className="inline-flex items-center gap-1 underline-offset-4 hover:underline"
            data-testid="dashboard-catalog"
          >
            <ScanBarcode className="size-4" aria-hidden /> {t('catalog.title')}
          </Link>
        )}
        {tastingEnabled && (
          <Link
            to="/tasting"
            className="inline-flex items-center gap-1 underline-offset-4 hover:underline"
            data-testid="dashboard-tasting"
          >
            <MapPin className="size-4" aria-hidden /> {t('tasting.title')}
          </Link>
        )}
      </section>
    </div>
  );
}
